import { SCREEN_BREAKPOINT } from "@lib/constants/AppConst";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

interface IScreenLayout {
  window: {
    width: number;
    breakpointCollapse: number;
    breakpointHide: number;
  };
  header: {
    height: number;
  };
  content: {
    width: number;
  };
  footer: {
    height: number;
    defaultHeight: number;
    minHeight: number;
    maxHeight: number;
    collapsed: boolean;
    enabled: boolean;
  };
  sidebar: {
    minWidth: number;
    maxWidth: number;
    width: number;
    defaultWidth: number;
    collapsed: boolean;
    hidden: boolean;
    dontOpen?: boolean;
  };
  rightPanel: {
    minWidth: number;
    maxWidth: number;
    width: number;
    defaultWidth: number;
    collapsed: boolean;
    enabled: boolean;
  };
}

const initialState: IScreenLayout = {
  window: {
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    breakpointCollapse: SCREEN_BREAKPOINT.tablet,
    breakpointHide: SCREEN_BREAKPOINT.mobile,
  },
  header: {
    height: 40,
  },
  content: {
    width: 1000,
  },
  footer: {
    height: 0,
    defaultHeight: 150,
    minHeight: 30,
    maxHeight: 500,
    collapsed: false,
    enabled: false,
  },
  sidebar: {
    minWidth: 50,
    maxWidth: 500,
    width: 250,
    defaultWidth: 250,
    collapsed: false,
    hidden: false,
    dontOpen: false,
  },
  rightPanel: {
    width: 0,
    minWidth: 0,
    maxWidth: 250,
    defaultWidth: 250,
    collapsed: false,
    enabled: false,
  },
} as const;

const slice = createSlice({
  name: "screen",
  initialState,
  reducers: {
    updateWindowSize: (state, action: PayloadAction<number>) => {
      if (!state.window) {
        // temporary fix
        state.window = {
          breakpointCollapse: initialState.window?.breakpointCollapse,
          breakpointHide: initialState.window?.breakpointHide,
          width: action.payload,
        };
      }

      const previousWidth = state.window.width;
      state.window.width = action.payload;

      // If dontOpen is true, prevent sidebar from opening
      if (state.sidebar.dontOpen) {
        // Keep sidebar closed/collapsed regardless of window size
        state.sidebar.width = 0;
        state.sidebar.collapsed = true;
        state.sidebar.hidden = true;
        return;
      }

      if (action.payload < state.window.breakpointHide) {
        if (!state.sidebar.hidden) {
          state.sidebar.width = 0;
        } else {
          state.sidebar.width = 0;
        }
        state.sidebar.collapsed = true;
      } else if (action.payload < state.window.breakpointCollapse) {
        state.sidebar.hidden = false;
        state.sidebar.collapsed = true;
        state.sidebar.width = state.sidebar.minWidth;
      } else {
        state.sidebar.hidden = false;
        if (previousWidth < state.window.breakpointCollapse) {
          state.sidebar.collapsed = false;
          state.sidebar.width = state.sidebar.defaultWidth;
        }
      }
    },
    updateSidebar: (
      state,
      action: PayloadAction<Partial<IScreenLayout["sidebar"]>>,
    ) => {
      // Update dontOpen if provided
      if (action.payload?.dontOpen !== undefined) {
        state.sidebar.dontOpen = action.payload.dontOpen;
      }

      // If dontOpen is true, prevent sidebar from opening
      if (state.sidebar.dontOpen) {
        // Allow updating dontOpen itself and allow closing/collapsing
        if (action.payload?.hidden !== undefined) {
          // Only allow setting hidden to true (closing)
          if (action.payload.hidden) {
            state.sidebar.hidden = true;
            state.sidebar.width = 0;
          }
          // If trying to set hidden to false (opening), ignore it
          return;
        }

        if (action.payload?.collapsed !== undefined) {
          // Only allow setting collapsed to true (collapsing)
          if (action.payload.collapsed) {
            state.sidebar.collapsed = true;
            state.sidebar.width = state.sidebar.minWidth;
          }
          // If trying to set collapsed to false (expanding), ignore it
          return;
        }

        // Prevent width changes that would open the sidebar
        if (action.payload?.width !== undefined) {
          // Only allow width changes that keep sidebar closed/collapsed
          if (action.payload.width <= state.sidebar.minWidth) {
            state.sidebar.width = action.payload.width;
            state.sidebar.collapsed = true;
          }
          // If trying to set width larger than minWidth (opening), ignore it
          return;
        }

        // If only dontOpen is being updated, return early
        if (
          Object.keys(action.payload).length === 1 &&
          action.payload.dontOpen !== undefined
        ) {
          return;
        }
      }

      if (action.payload?.hidden !== undefined) {
        state.sidebar.hidden = action.payload.hidden;

        if (action.payload.hidden) {
          state.sidebar.width = 0;
        } else {
          if (state.window.width < state.window.breakpointCollapse) {
            state.sidebar.collapsed = true;
            state.sidebar.width = state.sidebar.minWidth;
          } else {
            state.sidebar.collapsed = false;
            state.sidebar.width = state.sidebar.defaultWidth;
          }
        }
        return;
      }

      if (action.payload?.collapsed !== undefined) {
        state.sidebar.collapsed = action.payload.collapsed;

        if (action.payload.collapsed) {
          state.sidebar.width = state.sidebar.minWidth;
        } else {
          state.sidebar.width = state.sidebar.defaultWidth;
        }
      }

      if (action.payload?.width) {
        state.sidebar.width = action.payload.width;

        if (action.payload.width < state.sidebar.minWidth + 150) {
          state.sidebar.width = state.sidebar.minWidth;
          state.sidebar.collapsed = true;
        } else {
          state.sidebar.collapsed = false;
        }
      }
    },
    updateRightPanel: (
      state,
      action: PayloadAction<Partial<IScreenLayout["rightPanel"]>>,
    ) => {
      state.rightPanel = { ...state.rightPanel, ...action.payload };

      if (action.payload?.collapsed !== undefined) {
        state.rightPanel.collapsed = action.payload.collapsed;

        if (action.payload.collapsed) {
          state.rightPanel.width = state.rightPanel.minWidth;
        } else {
          state.rightPanel.width = state.rightPanel.defaultWidth;
        }
      }
    },
    updateFooter: (
      state,
      action: PayloadAction<Partial<IScreenLayout["footer"]>>,
    ) => {
      state.footer = { ...state.footer, ...action.payload };

      if (action.payload?.collapsed !== undefined) {
        state.footer.collapsed = action.payload.collapsed;

        if (action.payload.collapsed) {
          state.footer.height = state.footer.minHeight;
        } else {
          state.footer.height = state.footer.defaultHeight;
        }
      }
    },
    resetSidebar: (state) => {
      state.sidebar = { ...initialState.sidebar };
    },
  },
});

export default slice.reducer;
export const {
  updateSidebar,
  updateRightPanel,
  updateFooter,
  updateWindowSize,
  resetSidebar,
} = slice.actions;
